import React from 'react';
import { Box, Text, HStack, VStack } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { FiInfo } from 'react-icons/fi';
import ModalLayout from '@components/Modal/ModalLayout';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DynamicInputModal from '@components/DynamicInputModal';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import { getAddRangeFormConfig } from './formConfig';

// Confirm Update Modal
export const ConfirmUpdateModal = ({ isOpen, onClose, isLoading, onConfirm, message }) => (
  <ModalLayout
    isOpen={isOpen}
    onClose={onClose}
    textAlign="center"
    size="md"
  >
    <ConfirmModal
      handleSubmit={onConfirm}
      prompt={message}
      isLoading={isLoading}
    />
  </ModalLayout>
);

// Smart Range Feature Note Component
const SmartRangeFeatureNote = () => (
  <Box
    bg="blue.900"
    borderColor="blue.500"
    borderWidth="1px"
    borderRadius="md"
    p={3}
    mb={4}
  >
    <HStack align="flex-start" spacing={3}>
      <Box color="blue.400" mt={0.5}>
        <FiInfo size={16} />
      </Box>
      <VStack align="flex-start" spacing={1}>
        <Text color="blue.200" fontSize="xs" lineHeight="1.4">
          Ranges will be automatically reorganized and optimized when you add a new range.
          This ensures no overlaps and maintains proper sequencing for efficient processing.
        </Text>
      </VStack>
    </HStack>
  </Box>
);

// Add Range Modal
export const AddRangeModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  timeUnit = 'minutes',
  convertInputToMinutes
}) => {
  const dispatch = useDispatch();

  const handleSubmit = async (formData) => {
    // Validate that min and max amounts are not the same
    const minAmount = parseFloat(formData.minAmount);
    const maxAmount = formData.maxAmount === '' ? null : parseFloat(formData.maxAmount);

    if (maxAmount !== null && minAmount === maxAmount) {
      dispatch(
        setAlert({
          title: 'Maximum amount cannot be the same as minimum amount',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    // Additional validation: max should be greater than min
    if (maxAmount !== null && maxAmount < minAmount) {
      dispatch(
        setAlert({
          title: 'Maximum amount must be greater than minimum amount',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    // Transform the form data to match the expected format
    // Convert ETA to minutes for storage
    const transformedData = {
      minAmount: formData.minAmount,
      maxAmount: formData.maxAmount === '' ? null : formData.maxAmount,
      eta: convertInputToMinutes(formData.eta),
      isUnbounded: formData.maxAmount === '' || formData.maxAmount === null
    };

    await onSubmit(transformedData);
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose}>
      <VStack spacing={0} align="stretch">
        <Box px={6} pt={6} pb={2}>
          <Text fontSize="lg" fontWeight="semibold" color="white" mb={4}>
            Add New Amount Range
          </Text>
          <SmartRangeFeatureNote />
        </Box>
        <DynamicInputModal
          title=""
          onClose={onClose}
          onSubmit={handleSubmit}
          fields={getAddRangeFormConfig(timeUnit)}
          isLoading={isLoading}
        />
      </VStack>
    </ModalLayout>
  );
};
